import cn from 'classnames'
import Image from 'next/image'
import {FC, ReactNode} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {AVATAR_SIZE_LARGE, AVATAR_SIZE_MEDIUM, AVATAR_SIZE_SMALL} from '@/theme/sizing'
import {theme} from '@/theme/theme'

export enum AvatarSize {
  small = 'small',
  medium = 'medium',
  large = 'large',
}

const getAvatarSize = (size: AvatarSize) => {
  switch (size) {
    case AvatarSize.small:
      return AVATAR_SIZE_SMALL
    case AvatarSize.medium:
      return AVATAR_SIZE_MEDIUM
    default:
      return AVATAR_SIZE_LARGE
  }
}

type AvatarProps = {
  initials?: string
  photo?: {
    src: string
    alt: string
  }
  icon?: ReactNode
  backgroundColor?: string
  textColor?: string
  size?: AvatarSize
  isCircle?: boolean
}

export const Avatar: FC<React.PropsWithChildren<AvatarProps>> = ({
  initials,
  photo,
  icon,
  backgroundColor,
  textColor = theme.palette.text.white,
  size = AvatarSize.small,
  isCircle = true,
}) => {
  const avatarSize = getAvatarSize(size) as AvatarSize
  const textVariant =
    size === AvatarSize.small
      ? TextVariant.caption
      : size === AvatarSize.medium
      ? TextVariant.text14
      : TextVariant.text16

  return (
    <>
      <div className={cn('avatar', photo && 'photo')}>
        {icon ? (
          icon
        ) : photo ? (
          <Image
            src={photo.src}
            alt={photo.alt}
            style={{objectFit: 'contain'}}
            fill
            sizes={avatarSize}
          />
        ) : initials ? (
          <Text
            variant={textVariant}
            color={textColor}
            element="span"
            fontWeight={TextFontWeight.medium}
          >
            {initials}
          </Text>
        ) : null}
      </div>

      <style jsx>{`
        .avatar {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          width: ${avatarSize};
          height: ${avatarSize};
          flex: 0 0 ${avatarSize};
          border-radius: ${isCircle ? '50%' : '6px'};
          overflow: hidden;
          background-color: ${backgroundColor};
        }
        .photo {
          background-color: transparent;
        }
      `}</style>
    </>
  )
}
